import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Admin.scss";
import "./UnallocatedNetlinks.scss";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCopy, faSyncAlt } from "@fortawesome/free-solid-svg-icons";

import {
  compactDeviceId,
  filterDevices,
  formatAge,
  formatDateTime,
  formatUptime,
  modeLabel,
  sortDevices,
} from "../../utils/unallocated";

const API_BASE = "https://sucmhxbjlk.execute-api.us-east-1.amazonaws.com/prod";
const AUTO_REFRESH_MS = 30000;

const LOOKBACK_OPTIONS = [
  { days: 1, label: "Last 24 hours" },
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
];

const COLUMNS = [
  { key: "status", label: "Status" },
  { key: "deviceId", label: "Device ID" },
  { key: "lastSeen", label: "Last Seen" },
  { key: "firstSeen", label: "First Seen" },
  { key: "sourceIp", label: "Source IP" },
  { key: "firmwareVersion", label: "Firmware" },
  { key: "productType", label: "Product" },
  { key: "healthStatus", label: "Health" },
  { key: "uptime", label: "Uptime" },
  { key: "lastSample", label: "Last Sample", sortable: false },
  { key: "logCount", label: "Data Files" },
  { key: "checkinCount", label: "Check-ins" },
];

const SampleSummary = ({ sample }) => {
  if (!sample) return <span className="muted">No data</span>;
  return (
    <span>
      {modeLabel(sample.mode)}
      {sample.batteryVolt !== null && ` · ${sample.batteryVolt} V`}
      {sample.outputWatt !== null && ` · ${sample.outputWatt} W out`}
    </span>
  );
};

const DetailRow = ({ device }) => {
  const sample = device.latestSample;
  const health = device.health;
  const items = [
    ["Device ID (firmware format)", compactDeviceId(device.deviceId)],
    ["First seen", formatDateTime(device.firstSeen)],
    ["Last seen", formatDateTime(device.lastSeen)],
    ["Source IP", device.sourceIp ?? "Not recorded"],
    ["Last requested files", device.lastRequestedFiles ?? "-"],
    ["Last data file", device.lastLogFilename ? `${device.lastLogFilename} (${formatDateTime(device.lastLogUploadDate)})` : "-"],
    ["Sample time", formatDateTime(sample?.sampleTimestamp)],
    ["Mode", modeLabel(sample?.mode)],
    ["Battery", sample ? `${sample.batteryVolt ?? "-"} V, ${sample.batteryCurrent ?? "-"} A` : "-"],
    ["Input / Output", sample ? `${sample.inputWatt ?? "-"} W / ${sample.outputWatt ?? "-"} W` : "-"],
    ["Error code", sample?.errorCode ?? "-"],
    ["Health reported", formatDateTime(health?.reportedAt)],
    ["Modbus OK / Fail", health ? `${health.modbusSuccessCount ?? "-"} / ${health.modbusFailureCount ?? "-"}` : "-"],
    ["Uploads OK / Fail", health ? `${health.uploadSuccessCount ?? "-"} / ${health.uploadFailureCount ?? "-"}` : "-"],
    ["Upload state", health?.uploadState ?? "-"],
    ["Last event", health?.event || sample?.event || "-"],
  ];

  return (
    <tr className="detail-row">
      <td colSpan={COLUMNS.length}>
        <dl className="detail-grid">
          {items.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      </td>
    </tr>
  );
};

const UnallocatedNetlinks = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [days, setDays] = useState(7);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sortBy, setSortBy] = useState("lastSeen");
  const [sortDirection, setSortDirection] = useState("desc");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const loadDevices = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/logs/get/unallocated?days=${days}`);
      const payload = await response.json();
      if (!response.ok || payload.status !== "ok") {
        throw new Error(payload?.data?.error?.message || `HTTP ${response.status}`);
      }
      setDevices(payload.data.devices || []);
      setUpdatedAt(new Date());
      setError(null);
    } catch (err) {
      console.error("Error loading unallocated EVO-NetLinks:", err);
      setError(err.message || "Failed to load devices");
    } finally {
      setIsLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(loadDevices, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [autoRefresh, loadDevices]);

  const visibleDevices = useMemo(
    () => sortDevices(filterDevices(devices, { search, status }), sortBy, sortDirection),
    [devices, search, status, sortBy, sortDirection]
  );
  const onlineCount = devices.filter((d) => d.status === "online").length;

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(key);
      setSortDirection(["lastSeen", "firstSeen", "logCount", "checkinCount", "uptime"].includes(key) ? "desc" : "asc");
    }
  };

  const copyDeviceId = (event, deviceId) => {
    event.stopPropagation();
    navigator.clipboard?.writeText(compactDeviceId(deviceId));
  };

  return (
    <div className="admin-page unallocated-page">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate("/admin")}>
          <FontAwesomeIcon icon={faArrowLeft} /> Admin
        </button>
        <h2>Unallocated EVO-NetLinks</h2>
      </div>
      <p className="page-description">
        EVO-NetLinks that are sending data but are not assigned to an active product. Use this to
        confirm a unit is reporting in, or to help a customer find and register their device.
      </p>

      <div className="filter-bar">
        <input
          type="text"
          placeholder="Search Device ID, IP, firmware, product"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {LOOKBACK_OPTIONS.map((option) => (
            <option key={option.days} value={option.days}>
              {option.label}
            </option>
          ))}
        </select>
        <label className="auto-refresh">
          <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
          Auto-refresh (30 s)
        </label>
        <button onClick={loadDevices} disabled={isLoading}>
          <FontAwesomeIcon icon={faSyncAlt} spin={isLoading} /> Refresh
        </button>
      </div>

      <div className="summary-line">
        {visibleDevices.length} of {devices.length} devices shown · {onlineCount} online
        {updatedAt && ` · updated ${updatedAt.toLocaleTimeString()}`}
      </div>
      {error && <div className="error-banner">Could not load devices: {error}</div>}

      <div className="table-container">
        <table className="admin-table unallocated-table">
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th
                  key={column.key}
                  onClick={column.sortable === false ? undefined : () => handleSort(column.key)}
                  className={column.sortable === false ? "not-sortable" : undefined}
                >
                  {column.label}
                  {sortBy === column.key && (sortDirection === "asc" ? " ▲" : " ▼")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleDevices.map((device) => (
              <React.Fragment key={device.deviceId}>
                <tr
                  className={expandedId === device.deviceId ? "expanded" : undefined}
                  onClick={() => setExpandedId(expandedId === device.deviceId ? null : device.deviceId)}
                >
                  <td>
                    <span className={`status-pill status-${device.status}`}>
                      {device.status === "online" ? "Online" : "Offline"}
                    </span>
                  </td>
                  <td className="device-id">
                    {device.deviceId}
                    <button
                      className="copy-btn"
                      title="Copy Device ID"
                      onClick={(e) => copyDeviceId(e, device.deviceId)}
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                  </td>
                  <td title={formatDateTime(device.lastSeen)}>{formatAge(device.ageSeconds)}</td>
                  <td>{formatDateTime(device.firstSeen)}</td>
                  <td>{device.sourceIp ?? <span className="muted">-</span>}</td>
                  <td>{device.firmwareVersion ?? "-"}</td>
                  <td>{device.productType ?? "-"}</td>
                  <td>{device.health?.status ?? <span className="muted">-</span>}</td>
                  <td>{formatUptime(device.health?.uptimeSeconds)}</td>
                  <td>
                    <SampleSummary sample={device.latestSample} />
                  </td>
                  <td>{device.logCount}</td>
                  <td>{device.checkinCount}</td>
                </tr>
                {expandedId === device.deviceId && <DetailRow device={device} />}
              </React.Fragment>
            ))}
            {visibleDevices.length === 0 && !isLoading && (
              <tr>
                <td colSpan={COLUMNS.length} className="empty-row">
                  {devices.length === 0
                    ? "No unallocated EVO-NetLinks reported in this period."
                    : "No devices match the current filters."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {isLoading && devices.length === 0 && <p>Loading devices...</p>}
    </div>
  );
};

export default UnallocatedNetlinks;
